<template>
  <div :id="'draggable-list-'+type" :class="`draggable-list ${searchClass} ${containerClass}`">
    <form v-if="showSearch" class="search" @submit.prevent="onSearchList" v-on:keyup.enter="onSearchList">
      <input v-model="search" class="input-ui" type="text" :placeholder="$t('category_tags.search')">
      <button type="submit" @click.prevent="onSearchList">
        <span v-if="!isListLoading" class="fas fa-search icon-grey" aria-hidden="true"></span>
        <div v-else class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </button>
    </form>

    <div ref="draggableListItemsWrapper" class="draggable-list-items-wrapper">
      <app-load-content :loading="!data">
        <draggable-list-item v-for="(option, i) in orderedList" :key="`${option.id}-${i}`"
                           :isListLoading="isListLoading"
                           :isSelected="isSelected(option) || option.is_active"
                           :selected="selected"
                           :isDisabled="isItemDisabled(option)"
                           :option="option"
                           :orderBy="orderBy"
                           :isDraggable="isItemDraggable(option)"
                           :activeOption="activeOption"
                           :iconCss="iconCss"
                           :allowEdit="allowEdit"
                           :containerClass="containerClass"
                           :onDrag="onDrag"
                           :onDragEnd="onDragEnd"
                           :onSelect="onSelect"
                           :onSearch="onSearch"
                           :searchQuery="searchQuery"
                           :isInsight="isInsight"
                           @click.native="(e) => onSelectItem(e, { option, index: i })"
                           @dragstart.native="(e) => onDragItem(e, option, parentOption)"
                           @dragend.native.stop="onDragEnd"
                           @toggleExpandedOption="$emit('toggleExpandedOption', $event)"
                           @getSubOptions="$emit('getSubOptions', $event)"
                           @onRecordEdit="$emit('onRecordEdit', $event)"
                           @onRecordAdd="() => onRecordAdd(option)"
                           @onOpenSettings="$emit('onOpenSettings', $event)"
        >
          <strong v-if="option.id === 'all'">{{ $t('category_tags.any_tag') }}</strong>
          <span v-else>{{ option[orderBy] }}</span>
        </draggable-list-item>

        <div v-if="data && data.length === 0 && !isListLoading" class="draggable-list-item empty-state">
          {{$t('category_tags.empty')}}
        </div>

        <div v-if="allowAdd" class="draggable-list-item add align-center link-pointer" v-on:click="(e) => openAddModal(e)">
          <span :class="`fas fa-plus text-${containerClass}`" />
          {{$t('draggable_list.add_record')}}
        </div>

        <template v-if="isListLoading && hasMore">
          <app-list-loader />
        </template>
      </app-load-content>

      <div v-if="!isListLoading && hasMore && isInitiated" ref="observerBottomTarget"></div>
    </div>
  </div>
</template>

<script>
  import _ from 'lodash';
  // Mixins
  import infiniteLoad from "../../mixins/loaders/infiniteLoad";

  import LoadContent from '../load-content.vue';
  import ListLoader from '../list-loader';

  export default {
    components: {
      DraggableListItem: () => import('./draggable_list_item.vue'),
      'app-load-content': LoadContent,
      'app-list-loader': ListLoader
    },
    props: {
      type: {
        type: String,
        default: ''
      },
      data: Array,
      orderBy: {
        type: String,
        default: 'name'
      },
      containerClass: {
        type: String,
        default: ''
      },
      iconCss: {
        type: String,
        default: ''
      },
      searchable: {
        type: Boolean,
        default: false
      },
      isListLoading: {
        type: Boolean,
        default: false
      },
      isInsight:{
        type: Boolean,
        default: false
      },
      onSearch: Function,
      onSelect: Function,
      onDrag: {
        type: Function,
        default: () => null
      },
      onDragEnd: {
        type: Function,
        default: () => null
      },
      selected: {
        type: [Object, Array],
        default: () => []
      },
      allowEdit: {
        type: Boolean,
        default: false
      },
      allowAdd: {
        type: Boolean,
        default: false
      },
      activeOption: {
        type: Object,
        required: false
      },
      parentOption: {
        type: Object,
        required: false
      },
      searchQuery: {
        type: String,
        default: ''
      },
      pagination: {
        type: Object,
        required: false
      },
      isInitiated: {
        type: Boolean,
        required: false
      },
    },
    mixins: [ infiniteLoad ],
    computed: {
      showSearch () {
        return this.searchable && this.onSearch;
      },
      searchClass () {
        return this.showSearch ? 'draggable-list--search' : '';
      },

      orderedList () {
        let list = this.data.filter(item => item[this.orderBy])
        return _.orderBy(list, 'order');
      },

      paginationPage() { return typeof this.pagination === 'object' && (Object.hasOwn(this.pagination, 'currentPage') ? this.pagination?.currentPage : Object.hasOwn(this.pagination, 'page') ? this.pagination?.page : null) },
      hasMore() { return this.paginationPage < this.pagination?.totalPages; },

    },
    watch: {
      searchQuery: {
        handler(value) {
          if(this.search !== value) {
            this.searchQuery = value;
          }
        }
      },
      isListLoading(value) {
        if(!value && this.hasMore && this.isInitiated && !this.isListLoading) this.initiateInfiniteLoad();
      },
    },
    created () {
      this.search = this.searchQuery;
    },
    updated() {
      if (this.$refs.draggableListItemsWrapper && this.$refs.observerBottomTarget && !this.infiniteLoadIsInitiated && this.isInitiated) this.initiateInfiniteLoad();
    },
    data: function() {
      return {
        search: '',
        shiftSelected: {
          first: null,
          last: null,
        },
      }
    },
    methods: {
      onSelectItem (event, { option, index }) {
        if (event.shiftKey) {
          this.shiftSelect(event, index);
        } else {
          this.shiftSelected.first = index;
          this.shiftSelected.last = null;

          this.callOnSelect (event, option, index);
        }
      },
      shiftSelect(event, index) {
        let first = this.shiftSelected.first;
        let last = this.shiftSelected.last;

        if (
          !Number.isInteger(first) &&
          !Number.isInteger(last)
        ) {
          first = index;
        } else if (
          Number.isInteger(first) &&
          !Number.isInteger(last) &&
          first < index
        ) {
          last = index;    
        } else if (
          Number.isInteger(first) &&
          !Number.isInteger(last) &&
          first > index
        ) {
          last = first;
          first = index;
        } else if (
          Number.isInteger(first) &&
          Number.isInteger(last) &&
          index < first
        ) {
          first = index;
        } else if (
          Number.isInteger(first) &&
          Number.isInteger(last) &&
          index > first
        ) {
          last = index;
        }

        this.callOnSelect(event, this.orderedList[index]);

        if (
          Number.isInteger(first) && 
          Number.isInteger(last) &&
          last - first
        ) {
          for (let n = first + 1; n < last; n++)
            this.callOnSelect(event, this.orderedList[n]);

          this.shiftSelected.first = null;
          this.shiftSelected.last = null;
        } else {
          this.shiftSelected.first = first;
          this.shiftSelected.last = last;        
        }
      },
      onSearchList () {
        if (this.onSearch && typeof this.onSearch === 'function') {
          this.onSearch(this.search);
        }
      },
      callOnSelect (event, option) {
        if (this.isExpandable(option) || this.isItemDisabled(option)) {
          return;
        }
        if (this.onSelect && typeof this.onSelect === 'function') {
          this.onSelect({ option, event, parentOption: this.parentOption });
        }
      },
      isSelected(value) {
        const { selected } = this;

        if (_.isArray(selected)) {
          return selected.find(sel => sel.id === value.id) !== undefined;
        }
        else {
          return selected && value && selected.id === value.id;
        }
      },
      isItemDisabled (option) {
        return (this.type === 'category' && option.hasChildren === false) || (this.type === 'standard-breakdown' && this.isExpandable(option) && option.has_children === false && !option.allow_add);
      },
      isItemDraggable (option) {
        if(Reflect.has(option, 'isDraggable')) return option.isDraggable;
        return !this.isItemDisabled(option) && !_.isEmpty(option.draggable_to) && (this.type !== 'standard-breakdown' || (this.type === 'standard-breakdown' && option.has_children !== false));
      },
      onDragItem(e, dt, parentOption) {
        if (!this.isItemDraggable(dt)) {
          return;
        }
        e.stopPropagation();
        
        if (_.isArray(this.selected)) {
          const value = _.uniq([...this.selected, dt]);
          this.onDrag(e, value, parentOption);
        }
        else {
          this.onDrag(e, dt, parentOption);
        }
      },

      openAddModal(e) {
        e.preventDefault();
        e.stopPropagation();

        this.$emit('onRecordAdd');
      },

      onRecordAdd (option) {
        this.$emit('onRecordAdd', option)
      },
      isExpandable(option = {}) {
        return option.expandable || option.searchable;
      },
      initiateInfiniteLoad() {
        this.$nextTick(() => {
          if(this.infiniteLoadIsInitiated) this.infiniteLoadUnobserve(this.$refs.observerBottomTarget);
          this.initInfiniteLoad(this.$refs.observerBottomTarget, {root: this.$refs.draggableListItemsWrapper, rootMargin: "200px", threshold: 0.1});
        });
      }
    },
  }
</script>

<style lang="scss">
  @import "../../styles/components/coding-queries/draggable-list";
</style>